import { useState, useEffect } from "react";
import { useOnePartySupMast, useCreateGroup, useUpdateGroup } from "http-hooks/partysupmast/usepartysupmast";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Modal from "components/ui/modals";

const typeOptions = [
  { value: "V", label: "Vendor" },
  { value: "C", label: "Customer" },
];

export default function SetGroup({ id, onClose, open, defaultType = "" }) {
  const [groupDetails, setGroupDetails] = useState({
    group_code: "",
    group_name: "",
    select_type: defaultType,
  });
  const [selectedType, setSelectedType] = useState(
    typeOptions.find((o) => o.value === defaultType) || null
  );

  const { isLoading: isLoadingGroup, data } = useOnePartySupMast(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateGroup();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateGroup();

  const isPending = isCreating || isUpdating;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGroupDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setGroupDetails((prev) => ({ ...prev, select_type: type?.value || "" }));
  };

  useEffect(() => {
    if (data) {
      setGroupDetails({
        group_code: data.grpId || "",
        group_name: data.name || "",
        select_type: data.type || "",
      });
      setSelectedType(typeOptions.find((option) => option.value === data.type) || null);
    }
  }, [data]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        group_code: groupDetails.group_code.trim(),
        group_name: groupDetails.group_name.trim(),
        select_type: groupDetails.select_type,
      };
      if (id) {
        await mutateUpdate({ id, data: submitData });
      } else {
        await mutateCreate(submitData);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Group
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-group-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingGroup ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
        </div>
      ) : (
        <form id="set-group-form" onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            name="group_code"
            id="group_code"
            label="Group Code"
            maxLength="4"
            value={groupDetails.group_code}
            onChange={handleInputChange}
            disabled={!!id}
            required
          />
          <Input
            type="text"
            name="group_name"
            id="group_name"
            label="Group Name"
            maxLength="30"
            value={groupDetails.group_name}
            onChange={handleInputChange}
            required
          />
          <Select
            options={typeOptions}
            value={selectedType}
            onChange={handleTypeChange}
            label="Type"
            placeholder="Select Type"
            required
          />
        </form>
      )}
    </Modal>
  );
}
